import { createI18n } from 'vue-i18n'

const messages = {
  zh: {
    nav: {
      home: '首页',
      features: '核心特色',
      modes: '游戏模式',
      weapons: '真实枪械',
      download: '立即下载',
      language: '语言'
    },
    hero: {
      badge: '产品官网',
      title: '蛟龙行动',
      subtitle: 'Dragon Ops · FPS/TPS 战术射击',
      desc: '一人闯关突破极限，红蓝方对战燃爆全场。FPS与TPS双模式自由切换，写实枪械手感、真实弹道模拟、战术配合制胜 —— 这里是属于战士的战场。',
      tags: ['FPS/TPS切换', '一人闯关', '红蓝方对战', '战术配合'],
      cta: '立即下载'
    },
    features: {
      title: '核心特色',
      subtitle: '极致射击体验，战术决定胜负',
      items: [
        { title: 'FPS/TPS自由切换', desc: '一键切换第一人称与第三人称视角，不同场景选择最合适的视角，FPS的沉浸感与TPS的视野优势兼得。', icon: 'swap' },
        { title: '真实弹道模拟', desc: '子弹下坠、穿透衰减、后坐力控制 —— 每把枪都有独立的弹道模型，高手与菜鸟一目了然。', icon: 'target' },
        { title: '精工地图设计', desc: '从城市废墟到热带丛林，每张地图都经过数百次测试打磨，确保平衡性与战术深度。', icon: 'map' },
        { title: '排位竞技', desc: '完善的ELO匹配系统与赛季排位机制，从青铜到王者，每一段晋升都充满成就感。', icon: 'medal' },
        { title: '深度枪械定制', desc: '数十种枪械搭配上百种配件：瞄具、枪口、握把、弹匣……打造属于你的专属武器。', icon: 'gun' },
        { title: '内置语音系统', desc: '低延迟队内语音，战术沟通零障碍。配合小地图标点系统，团队协作效率翻倍。', icon: 'mic' }
      ]
    },
    modes: {
      solo: {
        title: '一人闯关模式',
        desc: '精心设计的单人战役关卡，从潜入暗杀到正面突击，多种通关策略。BOSS战考验你的枪法与走位，每一关都是一次肾上腺素飙升的挑战。',
        tags: ['线性剧情关卡', '多难度选择', '武器收集与升级', '全球排行榜']
      },
      pvp: {
        title: '红蓝方对战模式',
        desc: '5v5 / 10v10 团队对抗，红蓝双方在精心设计的地图中展开战术博弈。团队配合、信息共享、战术执行 —— 真正的胜利属于默契的队伍。',
        tags: ['据点争夺', '爆破模式', '团队死斗', '排位赛季']
      }
    },
    weapons: {
      title: '真实枪械系统',
      subtitle: '每一把枪都有独特的灵魂',
      items: [
        { title: '突击步枪', desc: '中距离全能型，适合大多数战斗场景' },
        { title: '狙击步枪', desc: '超远距离一击致命，制高点掌控全局' },
        { title: '冲锋枪', desc: '近距离高射速，巷战与室内之王' },
        { title: '霰弹枪', desc: '贴脸秒杀，突破手的第一选择' }
      ]
    },
    download: {
      title: '立即加入战斗',
      subtitle: '免费下载，跨平台同步作战',
      pc: 'Windows 下载',
      android: 'Android 下载',
      ios: 'iOS 下载',
      hint: '支持 Windows / Android / iOS 三端数据互通'
    },
    footer: {
      copyright: '© 2026 蛟龙行动 Dragon Ops. All rights reserved.',
      links: ['用户协议', '隐私政策', '联系我们', '官方社区']
    }
  },
  en: {
    nav: {
      home: 'Home',
      features: 'Features',
      modes: 'Modes',
      weapons: 'Arsenal',
      download: 'Download',
      language: 'Language'
    },
    hero: {
      badge: 'Official Site',
      title: 'Dragon Ops',
      subtitle: 'FPS / TPS Tactical Shooter',
      desc: 'Break your limits in solo campaigns, or ignite the battlefield in Red vs. Blue warfare. Switch freely between FPS and TPS, experience realistic gun handling, authentic ballistics simulation, and win with tactics — this is the battlefield for true warriors.',
      tags: ['FPS/TPS Switch', 'Solo Campaign', 'Red vs Blue', 'Tactical Teamplay'],
      cta: 'Download Now'
    },
    features: {
      title: 'Core Features',
      subtitle: 'Ultimate shooting experience, tactics decide victory',
      items: [
        { title: 'FPS/TPS Free Switch', desc: 'Switch between first-person and third-person views with one click. Choose the best angle for every scenario and enjoy both FPS immersion and TPS awareness.', icon: 'swap' },
        { title: 'Realistic Ballistics', desc: 'Bullet drop, penetration decay, and recoil control — every gun has its own ballistic model, separating pros from rookies.', icon: 'target' },
        { title: 'Crafted Map Design', desc: 'From urban ruins to tropical jungles, every map is polished through hundreds of tests to ensure balance and tactical depth.', icon: 'map' },
        { title: 'Ranked Competition', desc: 'A refined ELO matchmaking system and seasonal ranking ladder. From Bronze to King, every promotion feels rewarding.', icon: 'medal' },
        { title: 'Deep Gun Customization', desc: 'Dozens of firearms paired with hundreds of attachments: scopes, muzzles, grips, magazines... build your own weapon.', icon: 'gun' },
        { title: 'Built-in Voice Chat', desc: 'Low-latency team voice chat for seamless tactical communication. Combined with map markers, teamwork efficiency doubles.', icon: 'mic' }
      ]
    },
    modes: {
      solo: {
        title: 'Solo Campaign Mode',
        desc: 'Carefully designed single-player campaign levels, from stealth assassinations to frontal assaults. Boss battles test your aim and positioning — every level is an adrenaline-fueled challenge.',
        tags: ['Linear Story Levels', 'Multiple Difficulties', 'Weapon Collection & Upgrade', 'Global Leaderboard']
      },
      pvp: {
        title: 'Red vs Blue Warfare',
        desc: '5v5 / 10v10 team battles where Red and Blue clash in meticulously designed maps. Team coordination, information sharing, and tactical execution — victory belongs to the team that moves as one.',
        tags: ['Stronghold Contest', 'Demolition Mode', 'Team Deathmatch', 'Ranked Season']
      }
    },
    weapons: {
      title: 'Realistic Arsenal',
      subtitle: 'Every gun has a unique soul',
      items: [
        { title: 'Assault Rifle', desc: 'A versatile mid-range choice for most combat scenarios' },
        { title: 'Sniper Rifle', desc: 'One shot, one kill from extreme range; control the high ground' },
        { title: 'Submachine Gun', desc: 'High rate of fire up close, king of urban and indoor fights' },
        { title: 'Shotgun', desc: 'Instant takedowns at point-blank range, the breacher’s first choice' }
      ]
    },
    download: {
      title: 'Join the Battle Now',
      subtitle: 'Free download, cross-platform warfare',
      pc: 'Download for Windows',
      android: 'Download for Android',
      ios: 'Download for iOS',
      hint: 'Data sync across Windows, Android, and iOS'
    },
    footer: {
      copyright: '© 2026 Dragon Ops. All rights reserved.',
      links: ['Terms of Service', 'Privacy Policy', 'Contact Us', 'Community']
    }
  }
}

const i18n = createI18n({
  legacy: false,
  locale: 'zh',
  fallbackLocale: 'en',
  messages
})

export default i18n
